import { z } from 'zod';
import { LEAD_SOURCES, OPPORTUNITY_STAGES } from '../enums.js';
import { EntityMetaSchema, zEnum, zNonEmpty } from './common.js';

/**
 * Opportunity — a potential sale that groups one customer need and its estimate(s) (Phase II P2-M1).
 * Sits between Lead (top of funnel) and Estimate/Job. Preserves attribution from the originating lead.
 */
export const OpportunitySchema = EntityMetaSchema.extend({
  customerId: z.string(),
  propertyId: z.string().optional(),
  leadId: z.string().optional(), // originating lead, for attribution continuity
  name: zNonEmpty.max(160),
  stage: zEnum(OPPORTUNITY_STAGES).default('open'),
  estimatedValueCents: z.number().int().min(0).optional(),
  estimateIds: z.array(z.string()).default([]),
  source: zEnum(LEAD_SOURCES).optional(),
  ownerUserId: z.string().optional(),
  notes: z.string().max(8000).optional(),
  lostReason: z.string().max(500).optional(),
  // Attribution carried through the lifecycle (Parts 31-33)
  utmSource: z.string().max(120).optional(),
  utmMedium: z.string().max(120).optional(),
  utmCampaign: z.string().max(120).optional(),
});
export type Opportunity = z.infer<typeof OpportunitySchema>;

export const CreateOpportunitySchema = z.object({
  customerId: zNonEmpty,
  propertyId: z.string().optional(),
  leadId: z.string().optional(),
  name: zNonEmpty.max(160),
  stage: zEnum(OPPORTUNITY_STAGES).optional(),
  estimatedValueCents: z.number().int().min(0).optional(),
  source: zEnum(LEAD_SOURCES).optional(),
  ownerUserId: z.string().optional(),
  notes: z.string().max(8000).optional(),
});
export type CreateOpportunityInput = z.infer<typeof CreateOpportunitySchema>;

export const UpdateOpportunitySchema = z.object({
  name: zNonEmpty.max(160).optional(),
  stage: zEnum(OPPORTUNITY_STAGES).optional(),
  propertyId: z.string().optional(),
  estimatedValueCents: z.number().int().min(0).optional(),
  ownerUserId: z.string().optional(),
  notes: z.string().max(8000).optional(),
  lostReason: z.string().max(500).optional(),
});
export type UpdateOpportunityInput = z.infer<typeof UpdateOpportunitySchema>;
